<script setup lang="ts">
import { Check as CheckIcon, ChevronDown as ChevronDownIcon, Users as UsersIcon } from '@lucide/vue'
import { computed, ref } from 'vue'

import BaseFilterChip from '@/components/ui/BaseFilterChip.vue'
import { useEventListener } from '@/composables/useEventListener'
import { useUiStore } from '@/stores/uiStore'
import type { WorkspaceOverviewSharingFilter } from '@/utils/workspaceOverviewFilters'

const props = withDefaults(
  defineProps<{
    variant?: 'panel' | 'floating'
  }>(),
  {
    variant: 'panel',
  },
)

const uiStore = useUiStore()

const sharingFilter = computed({
  get: () => uiStore.workspaceOverviewSharingFilter,
  set: (value: WorkspaceOverviewSharingFilter) => uiStore.setWorkspaceOverviewSharingFilter(value),
})

const sharingOptions: Array<{ label: string; value: WorkspaceOverviewSharingFilter }> = [
  { label: 'All', value: 'all' },
  { label: 'Personal', value: 'personal' },
  { label: 'Shared', value: 'shared' },
]

const activeSharingLabel = computed(() =>
  sharingFilter.value === 'all'
    ? 'Sharing'
    : (sharingOptions.find((option) => option.value === sharingFilter.value)?.label ?? 'Sharing'),
)
const activeSharingIcon = computed(() => {
  if (sharingFilter.value === 'shared') return UsersIcon
  return null
})
const getSharingOptionIcon = (value: WorkspaceOverviewSharingFilter) => {
  if (value === 'shared') return UsersIcon
  return null
}
const hasActiveFilters = computed(() => sharingFilter.value !== 'all')

const sharingMenuOpen = ref(false)

const sharingMenuStyle = ref<{ top?: string; bottom?: string; left: string }>({
  top: '0px',
  left: '0px',
})

const positionMenu = (
  event: MouseEvent,
  styleTarget: typeof sharingMenuStyle,
  minimumSpaceBelow: number,
) => {
  const button = event.currentTarget as HTMLElement
  const rect = button.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom

  if (spaceBelow >= minimumSpaceBelow) {
    styleTarget.value = { top: `${rect.bottom + 4}px`, left: `${rect.left}px` }
    return
  }

  styleTarget.value = {
    bottom: `${window.innerHeight - rect.top + 4}px`,
    left: `${rect.left}px`,
  }
}

const toggleSharingMenu = (event: MouseEvent) => {
  if (sharingMenuOpen.value) {
    sharingMenuOpen.value = false
    return
  }

  positionMenu(event, sharingMenuStyle, 160)
  sharingMenuOpen.value = true
}

const closeSharingMenu = () => {
  sharingMenuOpen.value = false
}

const selectSharingOption = (value: WorkspaceOverviewSharingFilter) => {
  sharingFilter.value = value
  sharingMenuOpen.value = false
}

const clearFilters = () => {
  sharingFilter.value = 'all'
  closeSharingMenu()
}

useEventListener(document, 'click', (event: Event) => {
  const target = event.target as HTMLElement | null
  if (!target?.closest('[data-access-menu]')) {
    closeSharingMenu()
  }
})
</script>

<template>
  <TransitionGroup name="overview-filter-chip" tag="div" class="flex items-center gap-2">
    <div key="sharing" class="relative shrink-0" data-access-menu>
      <BaseFilterChip
        :active="hasActiveFilters"
        :variant="props.variant"
        @click="toggleSharingMenu($event)"
      >
        <component v-if="activeSharingIcon" :is="activeSharingIcon" class="h-3.5 w-3.5 shrink-0" />
        {{ activeSharingLabel }}
        <ChevronDownIcon class="h-3 w-3 shrink-0" />
      </BaseFilterChip>
      <Teleport to="body">
        <div
          v-if="sharingMenuOpen"
          :style="sharingMenuStyle"
          data-access-menu
          class="fixed z-9999 w-40 overflow-hidden rounded-xl border border-black/[0.08] bg-white/92 py-1 shadow-md backdrop-blur-sm"
        >
          <button
            v-for="option in sharingOptions"
            :key="option.value"
            class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors hover:cursor-pointer"
            :class="
              sharingFilter === option.value
                ? 'bg-emerald-50 font-medium text-emerald-700'
                : 'text-gray-700 hover:bg-gray-50'
            "
            @click="selectSharingOption(option.value)"
          >
            <component
              :is="getSharingOptionIcon(option.value)"
              v-if="getSharingOptionIcon(option.value)"
              class="h-3.5 w-3.5 shrink-0 text-gray-500"
            />
            <span v-else class="h-3.5 w-3.5 shrink-0" />
            {{ option.label }}
            <CheckIcon
              v-if="sharingFilter === option.value"
              class="ml-auto h-3.5 w-3.5 shrink-0 text-emerald-600"
            />
          </button>
        </div>
      </Teleport>
    </div>

    <button
      v-if="hasActiveFilters"
      key="clear"
      class="shrink-0 text-xs font-normal text-gray-900 transition-colors hover:cursor-pointer hover:text-gray-700"
      type="button"
      @click="clearFilters"
    >
      Clear filters
    </button>
  </TransitionGroup>
</template>

<style scoped>
.overview-filter-chip-enter-active,
.overview-filter-chip-leave-active {
  transition:
    opacity 220ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    filter 200ms ease;
}

.overview-filter-chip-enter-from,
.overview-filter-chip-leave-to {
  opacity: 0;
  filter: blur(3px);
  transform: translateX(-10px) scaleX(0.84) scaleY(0.92);
}

.overview-filter-chip-move {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}
</style>
